/**
 * "Notes about your call" (plan step 3). Opens when a call ends; the call is
 * ALREADY logged on the lead's feed by then, so Skip loses nothing — Save adds
 * the outcome, the notes and, optionally, a follow-up.
 *
 * The lead is the one the server picked (the file Call was pressed on, else the
 * newest non-Lost/Bad file with that number). When the number matched several
 * files the rep can move it; when it matched none, they search for one — a call
 * with no lead has no feed line until they do.
 *
 * Follow-ups go to the caller. Assigning one to someone else is admin-only (the
 * server enforces it too).
 */

import React, { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useAction } from '../hooks/useAction';
import type { RingCentralCallLogged, RingCentralLeadLabel } from '../services/api';
import { formatJobNo } from '../lib/jobNumber';
import { todayInTz } from '../lib/time';

const leadName = (l: RingCentralLeadLabel | null | undefined) =>
  l ? (`${l.first_name ?? ''} ${l.last_name ?? ''}`.trim() || 'Unnamed') : '';
const leadText = (l: RingCentralLeadLabel) => `${leadName(l)} · ${formatJobNo(l)} · ${l.stage ?? '—'}`;

function durationText(sec: number | null | undefined): string {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  return m ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
}

const label: React.CSSProperties = { fontSize: '11px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '4px', display: 'block' };
const field: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: '13px', borderRadius: '6px',
  background: 'var(--crm-field)', color: 'var(--crm-text)', border: '1px solid var(--crm-field-line)',
};
const btn: React.CSSProperties = {
  padding: '7px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
  background: 'var(--crm-panel-2)', color: 'var(--crm-text)', border: '1px solid var(--crm-line)',
};
const primary: React.CSSProperties = { ...btn, background: 'var(--crm-accent)', color: 'var(--crm-on-fill)', border: '1px solid var(--crm-accent)', fontWeight: 600 };

export function CallNotesModal({ entry, onClose }: { entry: RingCentralCallLogged; onClose: (saved: RingCentralCallLogged | null) => void }) {
  const api = useApiClient();
  const { user } = useAuth() as any;
  const isAdmin = user?.role === 'admin';
  const save = useAction();
  const call = entry.call;

  const [leadId, setLeadId] = useState<string>(entry.lead?.id ?? '');
  const [outcome, setOutcome] = useState<string>(call.outcome ?? (call.result === 'no_answer' ? 'No answer' : call.result === 'answered' ? 'Connected' : ''));
  const [notes, setNotes] = useState<string>(call.notes ?? '');
  const [withFollowUp, setWithFollowUp] = useState(false);
  const [fuDate, setFuDate] = useState(() => todayInTz(user?.timezone || 'America/Toronto'));
  const [fuTime, setFuTime] = useState('');
  const [fuText, setFuText] = useState('Call back');
  const [fuUser, setFuUser] = useState<string>(user?.id ?? '');
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Lead search, for a call that matched no file (or the wrong one).
  const [q, setQ] = useState('');
  const [found, setFound] = useState<RingCentralLeadLabel[]>([]);
  const [picked, setPicked] = useState<RingCentralLeadLabel | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.getUsers().then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [api, isAdmin]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound([]); return; }
    const t = setTimeout(() => {
      api.searchLeads({ q: term, page_size: 8 })
        .then((r) => setFound((r.data?.rows || []).map((l: any) => ({ id: l.id, first_name: l.first_name, last_name: l.last_name, job_no: l.job_no, stage: l.stage }))))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [api, q]);

  const options: RingCentralLeadLabel[] = [
    ...entry.candidates,
    ...(picked && !entry.candidates.some((c) => c.id === picked.id) ? [picked] : []),
  ];

  const direction = call.direction === 'Inbound' ? 'Inbound' : 'Outbound';
  const otherNumber = direction === 'Outbound' ? call.to_number : call.from_number;

  const onSave = () => save.run(async (key) => {
    setError(null);
    if (withFollowUp && !leadId) { setError('Choose the lead before adding a follow-up.'); return; }
    try {
      const res = await api.withIdempotencyKey(key).saveRingCentralCallNotes(call.id, {
        outcome: outcome || null,
        notes,
        ...(leadId && leadId !== call.lead_id ? { lead_id: leadId } : {}),
        follow_up: withFollowUp
          ? { date: fuDate, time: fuTime || null, description: fuText, ...(isAdmin && fuUser ? { assigned_to_user_id: fuUser } : {}) }
          : null,
      });
      onClose(res.data ?? null);
    } catch (e: any) {
      setError(e?.message || 'Could not save the notes.');
    }
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2147483001, padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-label="Notes about your call"
           style={{ background: 'var(--crm-panel)', color: 'var(--crm-text)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '18px', width: 'min(460px, 100%)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>Notes about your call</div>
        <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: '14px' }}>
          {direction} · {otherNumber || 'unknown number'} · {durationText(call.duration_sec)}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <span style={label}>Lead</span>
          {options.length > 0 && (
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} style={field}>
              {!leadId && <option value="">— choose —</option>}
              {options.map((l) => <option key={l.id} value={l.id}>{leadText(l)}</option>)}
            </select>
          )}
          {options.length === 0 && <div style={{ fontSize: '12px', color: 'var(--crm-amber)', marginBottom: '6px' }}>No lead has this number. Find the file to log the call on it:</div>}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, job # or phone…" style={{ ...field, marginTop: options.length ? '6px' : 0 }} />
          {found.length > 0 && (
            <div style={{ border: '1px solid var(--crm-line)', borderRadius: '6px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto' }}>
              {found.map((l) => (
                <button key={l.id} type="button" onClick={() => { setPicked(l); setLeadId(l.id); setQ(''); setFound([]); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 9px', fontSize: '12px', background: 'transparent', color: 'var(--crm-text)', border: 'none', borderBottom: '1px solid var(--crm-line)', cursor: 'pointer' }}>
                  {leadText(l)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <span style={label}>Outcome</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {entry.outcomes.map((o) => (
              <button key={o} type="button" onClick={() => setOutcome(outcome === o ? '' : o)} aria-pressed={outcome === o}
                      style={{ ...btn, padding: '5px 10px', fontSize: '12px', ...(outcome === o ? { background: 'var(--crm-accent-tint)', border: '1px solid var(--crm-accent)', color: 'var(--crm-accent)', fontWeight: 600 } : null) }}>
                {o}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={label} htmlFor="rc-call-notes">Notes</label>
          <textarea id="rc-call-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} autoFocus
                    placeholder="What was said, what happens next…" style={{ ...field, resize: 'vertical' }} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: withFollowUp ? '8px' : '14px', cursor: 'pointer' }}>
          <input type="checkbox" checked={withFollowUp} onChange={(e) => setWithFollowUp(e.target.checked)} />
          Add a follow-up
        </label>
        {withFollowUp && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
            <div><span style={label}>Date</span><input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} style={field} /></div>
            <div><span style={label}>Time (optional)</span><input type="time" value={fuTime} onChange={(e) => setFuTime(e.target.value)} style={field} /></div>
            <div style={{ gridColumn: '1 / -1' }}><span style={label}>Task</span><input value={fuText} onChange={(e) => setFuText(e.target.value)} style={field} /></div>
            {isAdmin && users.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={label}>Assign to</span>
                <select value={fuUser} onChange={(e) => setFuUser(e.target.value)} style={field}>
                  {users.map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}{u.id === user?.id ? ' (me)' : ''}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        {error && <div role="alert" style={{ fontSize: '12px', color: 'var(--crm-red)', marginBottom: '10px' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={() => onClose(null)} style={btn} title="The call is already on the feed">Skip</button>
          <button type="button" onClick={onSave} disabled={save.pending} style={{ ...primary, ...(save.pending ? { opacity: 0.6, cursor: 'progress' } : null) }}>
            {save.pending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
